"use strict";
/**
 * Unified text normalization utilities
 * Combines backend and client text processing logic
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.containsCJKCharacters = containsCJKCharacters;
exports.isCJKLanguage = isCJKLanguage;
exports.normalizeCJKText = normalizeCJKText;
exports.normalizeTextForComparison = normalizeTextForComparison;
exports.normalizeSentenceForComparison = normalizeSentenceForComparison;
exports.normalizeWordForComparison = normalizeWordForComparison;
exports.normalizeForAudioSearch = normalizeForAudioSearch;
exports.normalizeTextContentForDisplay = normalizeTextContentForDisplay;
/**
 * CJK Character ranges:
 * - CJK Unified Ideographs: U+4E00-U+9FFF (Chinese characters/Kanji)
 * - CJK Extension A: U+3400-U+4DBF (Extended Chinese characters)
 * - Hiragana: U+3040-U+309F (Japanese phonetic script)
 * - Katakana: U+30A0-U+30FF (Japanese phonetic script)
 */
const CJK_REGEX = /[\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/;
/**
 * Detect if text contains CJK characters
 */
function containsCJKCharacters(text) {
    return CJK_REGEX.test(text);
}
/**
 * Detect if language is CJK based on text content
 */
function isCJKLanguage(text) {
    if (!text)
        return false;
    const cjkCharCount = (text.match(new RegExp(CJK_REGEX.source, "g")) || [])
        .length;
    const totalChars = text.replace(/\s+/g, "").length;
    // If more than 30% of non-space characters are CJK, consider it a CJK language
    return totalChars > 0 && cjkCharCount / totalChars > 0.3;
}
/**
 * Normalize CJK text for comparison
 */
function normalizeCJKText(text) {
    if (!text)
        return "";
    return (text
        .normalize("NFC")
        .toLowerCase()
        // Remove punctuation but keep CJK characters and spaces
        .replace(/[^\p{L}\p{N}\s\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/gu, "")
        .trim()
        .replace(/\s+/g, " "));
}
/**
 * Basic text normalization for comparison
 * - Normalizes Unicode
 * - Converts to lowercase
 * - Removes extra spaces
 */
function normalizeTextForComparison(text) {
    if (!text)
        return "";
    return text.normalize("NFC").toLowerCase().trim().replace(/\s+/g, " ");
}
/**
 * Advanced sentence normalization for comparison
 * - Handles CJK languages
 * - Removes punctuation
 * - Normalizes spaces
 */
function normalizeSentenceForComparison(sentence) {
    if (!sentence)
        return "";
    // For CJK languages, use specialized normalization
    if (containsCJKCharacters(sentence)) {
        return normalizeCJKText(sentence);
    }
    return sentence
        .normalize("NFC")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, "") // Remove all except letters, numbers, spaces
        .trim()
        .replace(/\s+/g, " ");
}
/**
 * Word-level normalization for comparison
 * - Removes all non-alphanumeric characters
 * - Handles Unicode properly
 */
function normalizeWordForComparison(word) {
    if (!word)
        return "";
    return word
        .normalize("NFC")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]/gu, "");
}
/**
 * Normalize text for audio search
 * More aggressive normalization for TTS matching
 */
function normalizeForAudioSearch(text) {
    return normalizeTextForComparison(text)
        .replace(/[^\p{L}\s]/gu, "")
        .replace(/\s+/g, "");
}
/**
 * Display-friendly text normalization
 * Less aggressive, preserves readability
 */
function normalizeTextContentForDisplay(text) {
    if (!text)
        return "";
    return text
        .normalize("NFC")
        .trim()
        .replace(/\s+/g, " ")
        .replace(/\n{3,}/g, "\n\n"); // Limit consecutive newlines
}
//# sourceMappingURL=textNormalization.js.map